import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faExternalLinkAlt,
  faFloppyDisk,
  faMinus,
  faRotate,
  faSliders,
  faTrash,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import MyPagination from "../Common/MyPagination";
import {
  createWorkflowFileDataStructure,
  deleteWorkflowFile,
  downloadWorkflowFile,
  getWorkflowFile,
  getWorkflowFileDataStructures,
  listWorkflowFileTags,
  listWorkflowFiles,
  updateWorkflowFile,
  uploadWorkflowFile,
  workflowFileNameAvailable,
} from "../proxyAPI";
import Logs from "../Dashboard/Logs";
import ApiDataCollection from "../API/ApiDataCollection";

const normaliseFilesResponse = (data) => data?.files || data?.Files || data || [];
const normaliseFilesTotal = (data) => Number(data?.total ?? data?.Total ?? normaliseFilesResponse(data).length);

const fileUuid = (file) => file.UUID || file.Uuid || file.uuid;
const fileLabel = (file) =>
  file.Label || file.label || file.Filename || file.filename || file.FileName || file.file_name || fileUuid(file);
const fileName = (file) => file.Filename || file.filename || file.FileName || file.file_name || fileLabel(file);
const splitFileName = (name) => {
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === name.length - 1) {
    return { baseName: name, extension: "" };
  }
  return { baseName: name.slice(0, lastDot), extension: name.slice(lastDot) };
};
const fileBaseName = (file) => splitFileName(fileName(file)).baseName;
const fileSize = (file) =>
  file.SizeBytes ??
  file.size_bytes ??
  file.sizeBytes ??
  file.Size ??
  file.size ??
  file.Bytes ??
  file.bytes ??
  null;
const fileTemporary = (file) => file.Temporary ?? file.temporary ?? false;
const fileScope = (file) => {
  if (fileTemporary(file)) return "temporary";
  return file.Scope || file.scope || "permanent";
};
const fileCreatedBy = (file) => file.CreatedBy || file.created_by || file.createdBy || "";
const fileCreatedAt = (file) => file.CreatedAt || file.created_at || file.createdAt || "";
const fileProjectId = (file) => file.ProjectID || file.project_id || file.projectId || "";
const fileProjectUuid = (file) => file.ProjectUUID || file.project_uuid || file.projectUuid || "";
const fileProjectName = (file) =>
  file.ProjectName || file.project_name || file.projectName || fileProjectUuid(file) || "";
const fileProcessId = (file) => file.ProcessID || file.process_id || file.processId || 0;
const fileProcessRunning = (file) =>
  file.ProcessRunning ?? file.process_running ?? file.processRunning ?? false;
const fileAssetImport = (file) => file.AssetImport || file.asset_import || file.assetImport || null;
const fileAssetExport = (file) => file.AssetExport || file.asset_export || file.assetExport || null;
const fileCreatedByProject = (file) =>
  file.CreatedByProject ?? file.created_by_project ?? file.createdByProject ?? false;
const fileLogResourceId = (file) =>
  file.LogResourceID || file.log_resource_id || file.logResourceId || fileUuid(file);
const fileDirectories = (file) => file.Directories || file.directories || [];
const directoryOptions = (directories) =>
  directories.map((directory) => ({ value: directory, label: directory }));
const optionsToDirectories = (options) => (options || []).map((option) => option.value).filter(Boolean);
const FILE_EXTENSION_OPTIONS = ["json", "csv", "pdf", "xls", "xlsx", "txt", "doc", "docx"].map((extension) => ({
  value: extension,
  label: extension.toUpperCase(),
}));
const normalizeExtension = (extension) => extension.replace(/^\./, "").trim().toLowerCase();
const extensionOptions = (extensions) =>
  extensions.map((extension) => ({ value: extension, label: extension.toUpperCase() }));
const optionsToExtensions = (options) =>
  (options || [])
    .map((option) => normalizeExtension(option.value))
    .filter(Boolean);
const fileExtension = (file) => normalizeExtension(file.Extension || file.extension || splitFileName(fileName(file)).extension);
const fileSupportsDataStructures = (file) => ["csv", "json"].includes(fileExtension(file));
const dataStructureFields = (preview) => preview?.fields || preview?.Fields || [];
const dataStructureMatches = (preview) => preview?.matches || preview?.Matches || [];
const dataStructureCanCreate = (preview) => preview?.can_create ?? preview?.CanCreate ?? false;
const dataStructureMessage = (preview) => preview?.message || preview?.Message || "";
const dataStructureUuid = (structure) => structure?.Uuid || structure?.uuid || structure?.UUID || "";
const dataStructureLabel = (structure) => structure?.Label || structure?.label || dataStructureUuid(structure);

const assetImportStatusLabel = (assetImport) => {
  if (!assetImport) return "";
  const status = assetImport.Status || assetImport.status || "";
  const rowsImported = assetImport.RowsImported ?? assetImport.rows_imported ?? 0;
  const rowsValid = assetImport.RowsValid ?? assetImport.rows_valid ?? 0;
  return `${status}${rowsImported || rowsValid ? ` (${rowsImported}/${rowsValid})` : ""}`;
};

const assetImportActive = (assetImport) => assetImport?.Active ?? assetImport?.active ?? false;
const assetExportStatusLabel = (assetExport) => {
  if (!assetExport) return "";
  const status = assetExport.Status || assetExport.status || "";
  const rows = assetExport.Rows ?? assetExport.rows ?? 0;
  return `${status}${rows ? ` (${rows})` : ""}`;
};

const assetExportActive = (assetExport) => assetExport?.Active ?? assetExport?.active ?? false;

const projectHref = (id) => (id ? `/workflows/library/logic/builder/${id}` : undefined);

const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined || bytes === "") return "Unavailable";
  const numericBytes = Number(bytes);
  if (!Number.isFinite(numericBytes)) return "Unavailable";
  if (numericBytes < 1024) return `${numericBytes} B`;
  if (numericBytes < 1024 * 1024) return `${Math.round(numericBytes / 1024)} KB`;
  return `${Math.round((numericBytes / (1024 * 1024)) * 10) / 10} MB`;
};

const formatUploadedAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const temporaryToScope = (value) => {
  if (value === "yes") return "temporary";
  if (value === "no") return "permanent";
  return "either";
};

const fileFromLocation = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    fileUuid: params.get("file") || "",
    projectUuid: params.get("file_project_uuid") || "",
  };
};

const updateFileLocation = (file, replace = false) => {
  const url = new URL(window.location.href);
  if (file) {
    url.searchParams.set("file", fileUuid(file));
    const projectUuid = fileProjectUuid(file);
    if (projectUuid) {
      url.searchParams.set("file_project_uuid", projectUuid);
    } else {
      url.searchParams.delete("file_project_uuid");
    }
  } else {
    url.searchParams.delete("file");
    url.searchParams.delete("file_project_uuid");
  }

  const method = replace ? "replaceState" : "pushState";
  window.history[method]({}, "", `${url.pathname}${url.search}${url.hash}`);
};

export default function WorkflowFilesList({
  projectUuid,
  projects = [],
  selectedProject,
  onProjectChange,
  loadProjectOptions,
}) {
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [temporary, setTemporary] = useState("any");
  const [createdByProject, setCreatedByProject] = useState("any");
  const [processRunning, setProcessRunning] = useState("any");
  const [tagFilters, setTagFilters] = useState([]);
  const [extensionFilters, setExtensionFilters] = useState([]);
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [uploadFile, setUploadFile] = useState();
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadDirectories, setUploadDirectories] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingLabels, setEditingLabels] = useState({});
  const [editingDirectories, setEditingDirectories] = useState({});
  const [nameAvailability, setNameAvailability] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [dataStructurePreview, setDataStructurePreview] = useState(null);
  const [dataStructureLoading, setDataStructureLoading] = useState(false);
  const [dataStructureCreating, setDataStructureCreating] = useState(false);
  const [selectedDataStructure, setSelectedDataStructure] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [pagination, setPagination] = useState({ page: 0, perPage: 10 });

  const filterKey = useMemo(
    () =>
      JSON.stringify({
        projectUuid,
        search,
        tagFilters,
        temporary,
        createdByProject,
        processRunning,
        createdFrom,
        createdTo,
        extensionFilters,
      }),
    [projectUuid, search, tagFilters, temporary, createdByProject, processRunning, createdFrom, createdTo, extensionFilters]
  );

  const filters = useMemo(
    () => ({
      projectUuid,
      search,
      tags: tagFilters,
      extensions: extensionFilters,
      scope: temporaryToScope(temporary),
      createdByProject,
      processRunning: createdByProject === "yes" ? processRunning : "any",
      createdFrom,
      createdTo,
      limit: pagination.perPage,
      offset: pagination.page * pagination.perPage,
    }),
    [projectUuid, search, tagFilters, extensionFilters, temporary, createdByProject, processRunning, createdFrom, createdTo, pagination]
  );

  useEffect(() => {
    setPagination((current) => (current.page === 0 ? current : { ...current, page: 0 }));
  }, [filterKey]);

  const refresh = async () => {
    setLoading(true);
    const data = await listWorkflowFiles(filters);
    const nextFiles = normaliseFilesResponse(data);
    setFiles(nextFiles);
    setTotal(normaliseFilesTotal(data));
    setSelectedFile((current) => {
      if (!current) return null;
      const currentUuid = fileUuid(current);
      return nextFiles.find((file) => fileUuid(file) === currentUuid) || current;
    });
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, [filters]);

  const selectedFileProjectUuid = selectedFile ? fileProjectUuid(selectedFile) : "";
  const selectedFileProjectForAction = selectedFileProjectUuid || projectUuid;

  const refreshDataStructurePreview = async (file = selectedFile) => {
    if (!file || !fileSupportsDataStructures(file)) {
      setDataStructurePreview(null);
      return;
    }

    setDataStructureLoading(true);
    const data = await getWorkflowFileDataStructures(fileUuid(file), { projectUuid: selectedFileProjectForAction });
    setDataStructurePreview(data || null);
    setDataStructureLoading(false);
  };

  useEffect(() => {
    refreshDataStructurePreview(selectedFile);
  }, [selectedFile ? fileUuid(selectedFile) : "", selectedFileProjectForAction]);

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    const data = await uploadWorkflowFile({
      file: uploadFile,
      filename: uploadLabel || uploadFile.name,
      directories: uploadDirectories,
    });
    setUploading(false);
    if (data) {
      toast.success("File uploaded");
      setUploadFile(undefined);
      setUploadLabel("");
      setUploadDirectories([]);
      setShowUploadModal(false);
      refresh();
    }
  };

  const loadTagOptions = async (inputValue) => {
    const data = await listWorkflowFileTags(inputValue);
    return directoryOptions(data?.data || data?.Data || []);
  };

  const closeConfirm = () => setConfirmAction(null);

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    await confirmAction.onConfirm();
    closeConfirm();
  };

  const openFileDetails = (file, replace = false) => {
    setSelectedFile(file);
    updateFileLocation(file, replace);
  };

  const closeFileDetails = () => {
    setSelectedFile(null);
    updateFileLocation(null);
  };

  useEffect(() => {
    const loadLinkedFile = async (replace = true) => {
      const linkedFile = fileFromLocation();
      if (!linkedFile.fileUuid) {
        setSelectedFile(null);
        return;
      }

      const data = await getWorkflowFile(linkedFile.fileUuid, {
        projectUuid: linkedFile.projectUuid,
      });
      if (data) {
        openFileDetails(data, replace);
      }
    };

    loadLinkedFile(true);
    const handlePopState = () => loadLinkedFile(true);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return (
    <div className="bg-white rounded-3 p-3">
      <div className="d-flex align-items-center gap-2 mb-3">
        <h5 className="m-0">Workflow files</h5>
        <div className="flex-grow-1" />
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setShowUploadModal(true)}
        >
          <FontAwesomeIcon icon={faUpload} className="me-2" />
          Upload
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm border-0"
          onClick={refresh}
          disabled={loading}
          title="Refresh files"
        >
          <FontAwesomeIcon icon={faRotate} />
        </button>
      </div>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-12 col-lg-3">
          <label className="form-label">Project</label>
          <AsyncSelect
            cacheOptions
            defaultOptions={projects}
            loadOptions={loadProjectOptions}
            value={selectedProject}
            onChange={(option) => onProjectChange?.(option)}
            className="react-select-container"
            classNamePrefix="react-select"
            placeholder="Search projects..."
            isClearable
            noOptionsMessage={() => "No projects found"}
          />
        </div>
        <div className="col-12 col-lg-3">
          <label className="form-label">Search</label>
          <input
            type="search"
            className="form-control"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="col-12 col-lg-3">
          <label className="form-label">Tags</label>
          <AsyncCreatableSelect
            isMulti
            cacheOptions
            defaultOptions
            loadOptions={loadTagOptions}
            value={directoryOptions(tagFilters)}
            onChange={(options) => setTagFilters(optionsToDirectories(options))}
            className="react-select-container"
            classNamePrefix="react-select"
            placeholder="Filter by tags..."
            noOptionsMessage={() => "No tags found"}
          />
        </div>
        <div className="col-12 col-md-4 col-lg-2">
          <label className="form-label">File type</label>
          <AsyncCreatableSelect
            isMulti
            cacheOptions
            defaultOptions={FILE_EXTENSION_OPTIONS}
            loadOptions={(inputValue) => {
              const searchTerm = normalizeExtension(inputValue);
              return Promise.resolve(
                FILE_EXTENSION_OPTIONS.filter((option) => option.value.includes(searchTerm))
              );
            }}
            value={extensionOptions(extensionFilters)}
            onChange={(options) => setExtensionFilters(optionsToExtensions(options))}
            className="react-select-container"
            classNamePrefix="react-select"
            placeholder="Filter by extension..."
            formatCreateLabel={(value) => `Use .${normalizeExtension(value)}`}
            isValidNewOption={(value) => normalizeExtension(value) !== ""}
            noOptionsMessage={() => "No file types found"}
          />
        </div>
        <div className="col-12 col-md-4 col-lg-2">
          <label className="form-label">Temporary</label>
          <select
            className="form-select"
            value={temporary}
            onChange={(event) => setTemporary(event.target.value)}
          >
            <option value="any">Any</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        <div className="col-12 col-md-4 col-lg-2">
          <label className="form-label">Created by project</label>
          <select
            className="form-select"
            value={createdByProject}
            onChange={(event) => {
              setCreatedByProject(event.target.value);
              if (event.target.value !== "yes") {
                setProcessRunning("any");
              }
            }}
          >
            <option value="any">Any</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {createdByProject === "yes" && (
          <div className="col-12 col-md-4 col-lg-2">
            <label className="form-label">Process running</label>
            <select
              className="form-select"
              value={processRunning}
              onChange={(event) => setProcessRunning(event.target.value)}
            >
              <option value="any">Any</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        )}
      </div>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-12 col-md-4 col-lg-2">
          <label className="form-label">Created from</label>
          <input
            type="date"
            className="form-control"
            value={createdFrom}
            onChange={(event) => setCreatedFrom(event.target.value)}
          />
        </div>
        <div className="col-12 col-md-4 col-lg-2">
          <label className="form-label">Created to</label>
          <input
            type="date"
            className="form-control"
            value={createdTo}
            onChange={(event) => setCreatedTo(event.target.value)}
          />
        </div>
      </div>

      {showUploadModal && (
        <>
          <div className="modal-backdrop show" style={{ zIndex: 1040 }} />
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered" role="document" style={{ position: "relative", zIndex: 1051 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Upload file</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setShowUploadModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">File</label>
                  <input
                    type="file"
                    className="form-control"
                    onChange={(event) => {
                      const selectedFile = event.target.files?.[0];
                      setUploadFile(selectedFile);
                      setUploadLabel(selectedFile?.name || "");
                    }}
                  />
                </div>
                <div>
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={uploadLabel}
                    onChange={(event) => setUploadLabel(event.target.value)}
                    disabled={!uploadFile}
                  />
                </div>
                <div className="mt-3">
                  <label className="form-label">Tags</label>
                  <AsyncCreatableSelect
                    isMulti
                    cacheOptions
                    defaultOptions
                    loadOptions={loadTagOptions}
                    value={directoryOptions(uploadDirectories)}
                    onChange={(options) => setUploadDirectories(optionsToDirectories(options))}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    placeholder="Add tag..."
                    noOptionsMessage={() => "No tags found"}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpload}
                  disabled={!uploadFile || uploading}
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
          </div>
        </>
      )}

      {files.length > 0 ? (
        <>
          <div className="table-responsive">
            <table className="table align-middle">
            <thead>
              <tr>
                <th>File</th>
                <th>Project</th>
                <th>Scope</th>
                <th>Asset import</th>
                <th>Asset export</th>
                <th>Tags</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => {
                const uuid = fileUuid(file);
                const rowProjectId = fileProjectId(file);
                const rowProjectUuid = fileProjectUuid(file);
                const rowProjectName = fileProjectName(file);
                const rowDirectories = fileDirectories(file);
                const rowAssetImport = fileAssetImport(file);
                const rowAssetExport = fileAssetExport(file);
                return (
                  <tr key={uuid}>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-start text-decoration-none"
                        onClick={() => openFileDetails(file)}
                      >
                        {fileName(file)}
                      </button>
                    </td>
                    <td>
                      {rowProjectId ? (
                        <a href={projectHref(rowProjectId)}>{rowProjectName}</a>
                      ) : rowProjectUuid ? (
                        rowProjectName
                      ) : (
                        <span className="text-muted">Not associated</span>
                      )}
                    </td>
                    <td className="text-capitalize">{fileScope(file)}</td>
                    <td>
                      {rowAssetImport ? (
                        <span className={assetImportActive(rowAssetImport) ? "text-primary" : "text-muted"}>
                          {assetImportStatusLabel(rowAssetImport)}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {rowAssetExport ? (
                        <span className={assetExportActive(rowAssetExport) ? "text-primary" : "text-muted"}>
                          {assetExportStatusLabel(rowAssetExport)}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {rowDirectories.length > 0 ? rowDirectories.join(", ") : <span className="text-muted">-</span>}
                    </td>
                    <td className="text-end text-nowrap">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm border-0"
                        onClick={() => openFileDetails(file)}
                        title="Open file details"
                      >
                        <FontAwesomeIcon icon={faSliders} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
          <MyPagination
            key={filterKey}
            total={total}
            defaultPerPage={10}
            perPageOptions={[10, 25, 50, 100]}
            onPaginate={(page, perPage) => setPagination({ page, perPage })}
          />
        </>
      ) : (
        <div className="text-center m-3 text-muted">
          {loading ? "Loading files" : "No files"}
        </div>
      )}

      {selectedFile && (
        <div
          className="offcanvas offcanvas-end show"
          tabIndex="-1"
          style={{ visibility: "visible", width: "min(960px, 100vw)" }}
        >
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">File details</h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={closeFileDetails}
            />
          </div>
          <div className="offcanvas-body">
            {(() => {
              const uuid = fileUuid(selectedFile);
              const logResourceId = fileLogResourceId(selectedFile);
              const { extension } = splitFileName(fileName(selectedFile));
              const editedLabel = editingLabels[uuid] ?? fileBaseName(selectedFile);
              const renamedFileName = `${editedLabel}${extension}`;
              const nameAvailabilityKey = `${uuid}:${renamedFileName}`;
              const nameAvailabilityState = nameAvailability[nameAvailabilityKey];
              const nameError = nameAvailabilityState === false ? `Name ${renamedFileName} already exists` : "";
              const isCheckingName = nameAvailabilityState === "checking";
              const isNameChanged = renamedFileName !== fileName(selectedFile);
              const currentDirectories = fileDirectories(selectedFile);
              const editedDirectories = editingDirectories[uuid] ?? currentDirectories;
              const detailProjectId = fileProjectId(selectedFile);
              const detailProjectUuid = fileProjectUuid(selectedFile);
              const detailProjectName = fileProjectName(selectedFile);
              const detailAssetImport = fileAssetImport(selectedFile);
              const detailAssetExport = fileAssetExport(selectedFile);
              const detailImportActive = assetImportActive(detailAssetImport);
              const detailExportActive = assetExportActive(detailAssetExport);
              return (
                <>
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={editedLabel}
                        onChange={(event) =>
                          setEditingLabels((prev) => ({
                            ...prev,
                            [uuid]: splitFileName(event.target.value).baseName,
                          }))
                        }
                        onBlur={async () => {
                          if (!isNameChanged) return;
                          setNameAvailability((prev) => ({ ...prev, [nameAvailabilityKey]: "checking" }));
                          const data = await workflowFileNameAvailable(renamedFileName, uuid);
                          setNameAvailability((prev) => ({
                            ...prev,
                            [nameAvailabilityKey]: data?.available ?? data?.Available ?? false,
                          }));
                        }}
                      />
                      {extension && <span className="input-group-text">{extension}</span>}
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={isCheckingName || !!nameError}
                        onClick={async () => {
                          if (nameError) return;
                          if (isNameChanged) {
                            setNameAvailability((prev) => ({ ...prev, [nameAvailabilityKey]: "checking" }));
                            const data = await workflowFileNameAvailable(renamedFileName, uuid);
                            const available = data?.available ?? data?.Available ?? false;
                            setNameAvailability((prev) => ({ ...prev, [nameAvailabilityKey]: available }));
                            if (!available) return;
                          }
                          setConfirmAction({
                            title: "Rename file",
                            message: `Rename this file to "${renamedFileName}"?`,
                            confirmLabel: "Rename",
                            confirmClassName: "btn btn-primary",
                            onConfirm: async () => {
                              const data = await updateWorkflowFile(uuid, {
                                project_uuid: selectedFileProjectForAction,
                                filename: renamedFileName,
                              });
                              if (data) {
                                toast.success("File updated");
                                refresh();
                              }
                            },
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faFloppyDisk} className="me-2" />
                        Save name
                      </button>
                    </div>
                    {isCheckingName && <div className="form-text">Checking name...</div>}
                    {nameError && <div className="text-danger small mt-1">{nameError}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Tags</label>
                    <div className="d-flex gap-2 align-items-start">
                      <div className="flex-grow-1">
                        <AsyncCreatableSelect
                          isMulti
                          cacheOptions
                          defaultOptions
                          loadOptions={loadTagOptions}
                          value={directoryOptions(editedDirectories)}
                          onChange={(options) =>
                            setEditingDirectories((prev) => ({
                              ...prev,
                              [uuid]: optionsToDirectories(options),
                            }))
                          }
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="Add tag..."
                          noOptionsMessage={() => "No tags found"}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => {
                          setConfirmAction({
                            title: "Update tags",
                            message: "Update tags for this file?",
                            confirmLabel: "Update",
                            confirmClassName: "btn btn-primary",
                            onConfirm: async () => {
                              const data = await updateWorkflowFile(uuid, {
                                project_uuid: selectedFileProjectForAction,
                                directories: editedDirectories,
                              });
                              if (data) {
                                toast.success("Tags updated");
                                refresh();
                              }
                            },
                          });
                        }}
                      >
                        Save tags
                      </button>
                    </div>
                  </div>

                  <dl className="row small mb-4">
                    <dt className="col-5">File</dt>
                    <dd className="col-7 text-break">{fileName(selectedFile)}</dd>
                    <dt className="col-5">Project</dt>
                    <dd className="col-7">
                      {detailProjectId ? (
                        <a href={projectHref(detailProjectId)}>{detailProjectName}</a>
                      ) : detailProjectUuid ? (
                        detailProjectName
                      ) : (
                        <span className="text-muted">Not associated</span>
                      )}
                    </dd>
                    <dt className="col-5">Scope</dt>
                    <dd className="col-7 text-capitalize">{fileScope(selectedFile)}</dd>
                    <dt className="col-5">Tags</dt>
                    <dd className="col-7 text-break">
                      {currentDirectories.length > 0 ? currentDirectories.join(", ") : "-"}
                    </dd>
                    <dt className="col-5">Size</dt>
                    <dd className="col-7">{formatFileSize(fileSize(selectedFile))}</dd>
                    <dt className="col-5">Uploaded by</dt>
                    <dd className="col-7 text-break">{fileCreatedBy(selectedFile) || "-"}</dd>
                    <dt className="col-5">Uploaded at</dt>
                    <dd className="col-7">{formatUploadedAt(fileCreatedAt(selectedFile)) || "-"}</dd>
                    <dt className="col-5">Created by project</dt>
                    <dd className="col-7">{fileCreatedByProject(selectedFile) ? "Yes" : "No"}</dd>
                    <dt className="col-5">Asset import</dt>
                    <dd className="col-7">
                      {detailAssetImport ? (
                        <span className={detailImportActive ? "text-primary" : "text-muted"}>
                          {assetImportStatusLabel(detailAssetImport)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </dd>
                    <dt className="col-5">Asset export</dt>
                    <dd className="col-7">
                      {detailAssetExport ? (
                        <span className={detailExportActive ? "text-primary" : "text-muted"}>
                          {assetExportStatusLabel(detailAssetExport)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </dd>
                    {fileProcessId(selectedFile) > 0 && (
                      <>
                        <dt className="col-5">Process</dt>
                        <dd className="col-7">#{fileProcessId(selectedFile)}</dd>
                        <dt className="col-5">Process running</dt>
                        <dd className="col-7">{fileProcessRunning(selectedFile) ? "Yes" : "No"}</dd>
                      </>
                    )}
                  </dl>

                  {fileSupportsDataStructures(selectedFile) && (
                    <div className="border rounded p-3 mb-4">
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <h6 className="mb-0">Data structures</h6>
                        {dataStructureLoading && <span className="text-muted small">Checking...</span>}
                      </div>
                      {dataStructurePreview ? (
                        <>
                          {dataStructureFields(dataStructurePreview).length > 0 && (
                            <div className="mb-3">
                              <div className="small text-muted mb-1">Fields</div>
                              <div className="d-flex flex-wrap gap-2">
                                {dataStructureFields(dataStructurePreview).map((field) => (
                                  <span key={field} className="badge text-bg-light border">
                                    {field}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {dataStructureMatches(dataStructurePreview).length > 0 ? (
                            <div>
                              <div className="small text-muted mb-1">Matching data structures</div>
                              <div className="list-group list-group-flush">
                                {dataStructureMatches(dataStructurePreview).map((structure) => {
                                  const structureUuid = dataStructureUuid(structure);
                                  return (
                                  <div key={structureUuid || dataStructureLabel(structure)} className="list-group-item px-0">
                                    <div className="d-flex align-items-start justify-content-between gap-2">
                                      <div>
                                        <div className="fw-semibold">{dataStructureLabel(structure)}</div>
                                        <div className="text-muted small">
                                          {(structure.Fields || structure.fields || []).length} fields
                                        </div>
                                      </div>
                                      {structureUuid && (
                                        <button
                                          type="button"
                                          className="btn btn-link btn-sm p-0 text-nowrap"
                                          onClick={() => setSelectedDataStructure(structure)}
                                        >
                                          Open
                                          <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-1" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <span className="text-muted small">
                                {dataStructureMessage(dataStructurePreview) || "no matching data structures found"}
                              </span>
                              {dataStructureCanCreate(dataStructurePreview) && (
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  disabled={dataStructureCreating}
                                  onClick={async () => {
                                    setDataStructureCreating(true);
                                    const data = await createWorkflowFileDataStructure(uuid, {
                                      projectUuid: selectedFileProjectForAction,
                                    });
                                    setDataStructureCreating(false);
                                    if (data) {
                                      toast.success("Data structure created");
                                      setDataStructurePreview(data);
                                      refreshDataStructurePreview(selectedFile);
                                    }
                                  }}
                                >
                                  {dataStructureCreating ? "Creating..." : "Create matching data structure"}
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-muted small">Checking data structures...</div>
                      )}
                    </div>
                  )}

                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        downloadWorkflowFile(uuid, { projectUuid: selectedFileProjectForAction }, fileName(selectedFile))
                      }
                    >
                      <FontAwesomeIcon icon={faDownload} className="me-2" />
                      Download
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      disabled={detailImportActive || detailExportActive}
                      title={
                        detailImportActive
                          ? "File cannot be deleted while asset import is processing"
                          : detailExportActive
                            ? "File cannot be deleted while asset export is processing"
                            : "Delete file"
                      }
                      onClick={() => {
                        setConfirmAction({
                          title: "Delete file",
                          message: `Delete "${fileName(selectedFile)}"? This cannot be undone.`,
                          confirmLabel: "Delete",
                          confirmClassName: "btn btn-danger",
                          onConfirm: async () => {
                            const data = await deleteWorkflowFile(uuid, {
                              projectUuid: selectedFileProjectForAction,
                            });
                            if (data !== null) {
                              toast.success("File deleted");
                              closeFileDetails();
                              refresh();
                            }
                          },
                        });
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Delete
                    </button>
                  </div>

                  <div className="mt-4">
                    <h5>Logs</h5>
                    <hr className="my-3 text-secondary" />
                    <div className="p-3 bg-white rounded-3">
                      <Logs kind="file" resource={logResourceId} />
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      {selectedFile && <div className="offcanvas-backdrop fade show" onClick={closeFileDetails} />}
      {selectedDataStructure && createPortal(
        <>
          <div className="modal-backdrop show" style={{ zIndex: 1900 }} />
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1901 }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document" style={{ position: "relative", zIndex: 1902 }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{dataStructureLabel(selectedDataStructure)}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setSelectedDataStructure(null)}
                  />
                </div>
                <div className="modal-body">
                  <ApiDataCollection
                    apiDataCollectionUUID={dataStructureUuid(selectedDataStructure)}
                    fieldModalClassName="workflow-file-data-field-modal"
                    fieldModalBackdropClassName="workflow-file-data-field-modal-backdrop"
                  />
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
      {confirmAction && createPortal(
        <>
          <div className="modal-backdrop show" style={{ zIndex: 2000 }} />
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ zIndex: 2001 }}>
            <div className="modal-dialog modal-dialog-centered" role="document" style={{ position: "relative", zIndex: 2002 }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{confirmAction.title}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeConfirm}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-0">{confirmAction.message}</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={closeConfirm}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={confirmAction.confirmClassName}
                    onClick={runConfirmedAction}
                  >
                    {confirmAction.confirmLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
